import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Waves } from "lucide-react";

export function Login() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, pw);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="flex justify-center mb-6"><Waves className="w-10 h-10 text-navy" strokeWidth={1.3} /></div>
        <h1 className="font-heading text-3xl text-navy text-center mb-2" data-testid="login-title">Welcome back</h1>
        <p className="text-center text-neutral-500 mb-8">Sign in to your inAirlie account</p>
        <form onSubmit={submit} className="space-y-4 bg-white p-8 rounded-2xl border border-black/5 shadow-sm">
          <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
          <Input type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="login-password" />
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-navy hover:bg-navy/90" data-testid="login-submit">
            {busy ? "Signing in..." : "Sign in"}
          </Button>
          <p className="text-center text-sm text-neutral-500">
            No account? <Link to="/register" className="text-navy font-medium hover:underline">Join free</Link>
          </p>
        </form>
      </div>
    </Layout>
  );
}

export function Register() {
  const [params] = useSearchParams();
  const inviteToken = params.get("invite") || null;
  const invitedEmail = params.get("email") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState(invitedEmail);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (invitedEmail) setEmail(invitedEmail); }, [invitedEmail]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(name, email, pw, inviteToken);
      toast.success(inviteToken ? "Welcome — venue linked to your account!" : "Account created!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="flex justify-center mb-6"><Waves className="w-10 h-10 text-navy" strokeWidth={1.3} /></div>
        <h1 className="font-heading text-3xl text-navy text-center mb-2" data-testid="register-title">
          {inviteToken ? "Claim your venue" : "Join inAirlie"}
        </h1>
        <p className="text-center text-neutral-500 mb-8">
          {inviteToken
            ? "Set a password to activate your account and manage your listing."
            : "Free. Submit venues, events & get featured."}
        </p>
        <form onSubmit={submit} className="space-y-4 bg-white p-8 rounded-2xl border border-black/5 shadow-sm">
          <Input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name" />
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={Boolean(inviteToken)}
            className={inviteToken ? "bg-sand/30" : undefined}
            data-testid="register-email"
          />
          <Input type="password" placeholder="Password (min 6)" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="register-password" />
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-navy hover:bg-navy/90" data-testid="register-submit">
            {busy ? "Creating account..." : (inviteToken ? "Activate my account" : "Create account")}
          </Button>
          <p className="text-center text-sm text-neutral-500">
            Already have one? <Link to="/login" className="text-navy font-medium hover:underline">Sign in</Link>
          </p>
        </form>
      </div>
    </Layout>
  );
}
